import { ComponentPropsWithoutRef, ReactNode } from "react";
import { useIntl } from "react-intl";
import { clsx } from "clsx";
import styles from "./PanelFrame.module.scss";
import MiscellaneousXSmall from "../../../../../../common/icons/MiscellaneousXSmall.tsx";
import ErrorBoundary from "../../../../../../common/core-ui/ErrorBoundary.tsx";
import usePanelController from "./usePanelController.ts";
import useIsMediaLessThan from "../../../../../../common/core-ui/useIsMediaLessThan.ts";
import FocusTrap from "focus-trap-react";
import { OpenerScrollMode } from "./panelTypes.ts";

function PanelCloseButton(props: ComponentPropsWithoutRef<"button">) {
  const intl = useIntl();

  return (
    <button
      className={clsx(
        "vst-icon-button-circle",
        "vst-icon-button-variant-black",
        "vst-icon-button-size-small",
        styles.closeButton
      )}
      aria-label={intl.formatMessage({ defaultMessage: "Close" })}
      data-tooltip-content={intl.formatMessage({
        defaultMessage: "Close",
      })}
      data-tooltip-place="left"
      {...props}
    >
      <MiscellaneousXSmall />
    </button>
  );
}

interface PanelFrameProps {
  children: ReactNode;
  onRequestClose: () => void;
  targetId: string;
  classNameInner?: string;
  classNameOuter?: string;
  scrollMode?: OpenerScrollMode;
}

/**
 * Set `--panel-margin-start` if you need to adjust the start margin to
 * accommodate the main content. This is a css var and not a prop because
 * it is typically determined by the page layout, not the panel opener.
 */
export default function PanelFrame({
  children,
  onRequestClose,
  targetId,
  classNameInner,
  classNameOuter,
  scrollMode,
}: PanelFrameProps) {
  const intl = useIntl();
  const { panelRef, renderPanel } = usePanelController(
    onRequestClose,
    targetId,
    { scrollMode }
  );
  const isFullScreenPanel = useIsMediaLessThan(768);

  const a11yProps = isFullScreenPanel
    ? {
        role: "dialog",
        "aria-modal": true,
        onKeyUp: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Escape") {
            onRequestClose();
          }
        },
      }
    : {
        role: "region",
        "aria-label": intl.formatMessage({
          defaultMessage: "Info Panel",
        }),
      };

  return renderPanel(
    <FocusTrap
      active={isFullScreenPanel}
      focusTrapOptions={{
        // Handled elsewhere
        initialFocus: false,
        returnFocusOnDeactivate: false,
        escapeDeactivates: false,
      }}
    >
      <div className={clsx(styles.panelOuter, classNameOuter)} {...a11yProps}>
        <div
          ref={panelRef}
          className={clsx(
            "vst-card",
            "vst-card-variant-raised",
            styles.panelInner,
            classNameInner
          )}
        >
          <PanelCloseButton onClick={onRequestClose} />
          <ErrorBoundary className={styles.error}>{children}</ErrorBoundary>
        </div>
      </div>
    </FocusTrap>
  );
}
